'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Trophy, DollarSign, Zap, Building2 } from 'lucide-react';
import { DatePicker } from '../ui/date-picker';
import { useStepperContext } from '../ui/stepper';
import { useSessionFooter } from './NewSessionStepper';

interface NewSessionStep1FormatProps {
  onComplete: (data: { format: string; universe: 'Online' | 'Live'; date?: Date }) => void;
  initialData?: any;
  /** Fermer le stepper (modal). Utilisé pour rediriger vers Evenements Live depuis l’ajout rapide. */
  onClose?: () => void;
  quickAddMode?: 'online' | 'live';
}

interface GameFormat {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  universe: 'Online' | 'Live';
  color: string;
  iconBg: string; // classe Tailwind pour le fond de l'icône (style Ajout rapide)
}

export const NewSessionStep1Format: React.FC<NewSessionStep1FormatProps> = ({ 
  onComplete, 
  initialData,
  onClose,
  quickAddMode,
}) => {
  const t = useTranslations('sessions.new.stepper.step1');
  const tFormats = useTranslations('settings.gameFormats');
  const { nextStep } = useStepperContext();
  const setFooter = useSessionFooter()?.setFooter;
  
  const [availableFormats, setAvailableFormats] = useState<GameFormat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFormat, setSelectedFormat] = useState<string>('');
  const [selectedUniverse, setSelectedUniverse] = useState<'Online' | 'Live'>('Online');
  const [date, setDate] = useState<Date | undefined>(initialData?.date || new Date());

  useEffect(() => {
    const fetchFormats = async () => {
      try {
        const response = await fetch('/api/user');
        if (response.ok) {
          const data = await response.json();
          const formats: GameFormat[] = [];
          
          // Formats Online (selon préférences utilisateur)
          if (data.gameFormats?.online) {
            if (data.gameFormats.online.mtt) {
              formats.push({
                id: 'MTT',
                label: t('formatLabelTournois'),
                icon: Trophy,
                universe: 'Online',
                color: 'from-blue-600 to-blue-800',
                iconBg: 'bg-blue-500',
              });
            }
            if (data.gameFormats.online.cashGame) {
              formats.push({
                id: 'Cash Game',
                label: tFormats('onlineFormats.cashGame'),
                icon: DollarSign,
                universe: 'Online',
                color: 'from-red-600 to-red-800',
                iconBg: 'bg-gold',
              });
            }
            if (data.gameFormats.online.spinGo) {
              formats.push({
                id: 'Spin & Go',
                label: tFormats('onlineFormats.spinGo'),
                icon: Zap,
                universe: 'Online',
                color: 'from-yellow-600 to-yellow-800',
                iconBg: 'bg-amber-500',
              });
            }
          }
          
          // LIVE : 2 choix fixes (TOURNOIS en premier, puis CASH GAME), indépendants des préférences
          formats.push({
            id: 'MTT',
            label: t('liveTournament'),
            icon: Trophy,
            universe: 'Live',
            color: 'from-blue-600 to-blue-800',
            iconBg: 'bg-blue-500',
            liveKind: 'tournament',
          } as GameFormat & { liveKind?: string });
          formats.push({
            id: 'Cash Game',
            label: t('liveSessionRoom'),
            icon: Building2,
            universe: 'Live',
            color: 'from-red-600 to-red-800',
            iconBg: 'bg-gold',
            liveKind: 'sessionRoom',
          } as GameFormat & { liveKind?: string });
          
          setAvailableFormats(formats);
        }
      } catch (error) {
        console.error('Error fetching formats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFormats();
  }, [tFormats, t]);

  const handleFormatSelect = (formatId: string, universe: 'Online' | 'Live') => {
    setSelectedFormat(formatId);
    setSelectedUniverse(universe);
  };

  const handleContinue = useCallback(() => {
    if (!selectedFormat || !date) return;
    // Session poker room (Cash Game Live) : on continue dans le stepper, plus de redirection
    onComplete({ format: selectedFormat, universe: selectedUniverse, date });
    nextStep();
  }, [selectedFormat, selectedUniverse, date, onComplete, nextStep]);

  useEffect(() => {
    if (!setFooter) return;
    setFooter({
      primary: {
        label: t('continue'),
        disabled: !selectedFormat || !date,
        onClick: handleContinue,
      },
    });
    return () => setFooter(null);
  }, [setFooter, t, selectedFormat, date, handleContinue]);

  const onlineFormats = availableFormats.filter(f => f.universe === 'Online');
  const liveFormats = availableFormats.filter(f => f.universe === 'Live');
  const showOnline = quickAddMode ? quickAddMode === 'online' : true;
  const showLive = quickAddMode ? quickAddMode === 'live' : true;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('dateLabel') || 'Date de la session'} *
        </label>
        <DatePicker
          value={date}
          onChange={setDate}
          placeholder={t('datePlaceholder') || 'Sélectionnez une date'}
          className="w-auto max-w-sm [&>button]:h-12 [&>button]:text-base"
          maxDate={new Date()}
        />
      </div>

      {/* Online Formats — même design que Ajout rapide */}
      {showOnline && onlineFormats.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
            {tFormats('online.title')}
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {tFormats('online.description')}
          </p>
          <div className={`grid gap-3 ${onlineFormats.length >= 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
            {onlineFormats.map((format) => {
              const Icon = format.icon;
              const isSelected = selectedFormat === format.id && selectedUniverse === 'Online';
              return (
                <button
                  key={`online-${format.id}`}
                  onClick={() => handleFormatSelect(format.id, 'Online')}
                  className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all group ${
                    isSelected
                      ? 'border-gold bg-gold/5 dark:bg-gold/10'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <div className={`${format.iconBg} w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{format.label}</h3>
                  </div>
                  <div className={`flex-shrink-0 ${isSelected ? 'text-gold' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`}>
                    {isSelected ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* LIVE : Session poker room ou Tournoi — tout dans le stepper */}
      {showLive && liveFormats.length > 0 && (
        <div className="mt-8">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
            {tFormats('live.title')}
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {tFormats('live.description')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {liveFormats.map((format) => {
              const Icon = format.icon;
              const isSelected = selectedFormat === format.id && selectedUniverse === 'Live';
              return (
                <button
                  key={`live-${format.id}-${(format as { liveKind?: string }).liveKind ?? ''}`}
                  onClick={() => handleFormatSelect(format.id, 'Live')}
                  className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all group ${
                    isSelected
                      ? 'border-gold bg-gold/5 dark:bg-gold/10'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <div className={`${format.iconBg} w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{format.label}</h3>
                  </div>
                  <div className={`flex-shrink-0 ${isSelected ? 'text-gold' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`}>
                    {isSelected ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
